/**
 * PII normalization rules, applied BEFORE SHA-256 hashing.
 *
 * One hash can never satisfy every platform at once (Google strips gmail dots,
 * Microsoft strips dots and plus-tags on all domains, Meta wants the address
 * as-is), so we produce named VARIANTS and each destination adapter picks its
 * own. This module is the single source of truth for both the tracking script
 * (Web Crypto hashing) and the backend webhook path (node crypto hashing):
 * pure string functions, no environment dependencies.
 *
 * Variant contract (userData keys):
 *   em    base email: trim, lowercase, all whitespace removed
 *   em_g  Google:     em + gmail/googlemail local-part dots and +suffix removed
 *   em_r  Reddit:     em + dots/+suffix removed on ALL domains, accents KEPT
 *   em_s  strict:     em + dots/+suffix removed on ALL domains + accents removed
 *   ph    digits:     country code + national digits, no leading zeros, no "+"
 *   ph_e  E.164:      "+" + the ph digits
 *   fn_o  OpenAI:     fn/ln with ALL whitespace removed, accents kept
 *   ln_o  (only sent when differing from the base fn/ln)
 * em_g/em_r/em_s are only sent when they differ from em (adapters fall back to
 * em).
 */

/** Strip diacritics: "rené" -> "rene". */
function stripAccents(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * Compose accented characters into their single-codepoint form.
 *
 * "é" can be one codepoint (U+00E9) or an "e" followed by a combining accent,
 * and the two hash to completely different values. macOS filesystems and some
 * form submissions produce the decomposed form, so without this the same name
 * matches or does not depending on where the visitor typed it.
 *
 * Meta publishes an example hash for "Valéry" and it is the composed form that
 * reproduces it. See the test.
 */
function composeAccents(value: string): string {
  return value.normalize("NFC");
}

/** Base email normalization: trim, lowercase, remove ALL whitespace. */
export function normalizeEmail(raw: string): string | null {
  const email = String(raw).trim().toLowerCase().replace(/\s+/g, "");
  if (!email.includes("@") || email.length < 3) return null;
  return email;
}

/** Split a normalized email into [localPart, domain]. */
function splitEmail(email: string): [string, string] {
  const at = email.lastIndexOf("@");
  return [email.slice(0, at), email.slice(at + 1)];
}

/** Remove a "+suffix" tag and (optionally) dots from an email local part. */
function stripLocalPart(local: string, stripDots: boolean): string {
  let result = local;
  const plusIdx = result.indexOf("+");
  if (plusIdx > 0) result = result.slice(0, plusIdx);
  if (stripDots) result = result.replace(/\./g, "");
  return result;
}

const GOOGLE_DOMAINS = ["gmail.com", "googlemail.com"];

/**
 * Google Data Manager variant: base rules, plus for gmail/googlemail
 * addresses all dots before the "@" and any "+suffix" are removed.
 */
export function normalizeEmailGoogle(raw: string): string | null {
  const email = normalizeEmail(raw);
  if (!email) return null;
  const [local, domain] = splitEmail(email);
  if (!GOOGLE_DOMAINS.includes(domain)) return email;
  return stripLocalPart(local, true) + "@" + domain;
}

/**
 * Reddit variant: base rules, plus dots before the "@" and "+suffix" removed
 * on ALL domains. Accents are NOT touched.
 *
 * Reddit states the rule and then proves it with a worked example: the address
 * must be lowercased, the local part must lose its dots and everything after a
 * plus sign, and the result hashed with SHA-256 into 64 lowercase hex digits.
 * Their example says `alice@example.com` and `Al.ice+Apple@Example.Com` both
 * hash to ff8d9819fc0e12bf0d24892e45987e249a28dce836a85cad60e28eaaa8c6d976,
 * which is exactly sha256("alice@example.com"). The test reproduces that.
 *
 * Note the demonstration domain: example.com, not gmail. So this is the
 * all-domains rule, which is why em_g (Google, gmail-only) does not fit.
 *
 * The reason this is not em_s either, and the reason it is worth its own
 * function rather than a reused one: em_s also strips ACCENTS, which Reddit
 * never asks for. On an address whose local part carries an accent but no dot
 * or plus tag, em_s would hand Reddit a hash of a spelling the address never
 * had, while the plain em happens to be exactly right. Reusing em_s would fix
 * one mismatch by introducing another.
 */
export function normalizeEmailReddit(raw: string): string | null {
  const email = normalizeEmail(raw);
  if (!email) return null;
  const [local, domain] = splitEmail(email);
  return stripLocalPart(local, true) + "@" + domain;
}

/**
 * Strict variant (Microsoft rules): base rules, plus dots before the "@" and
 * "+suffix" removed on ALL domains, and accents removed.
 */
export function normalizeEmailStrict(raw: string): string | null {
  const email = normalizeEmail(raw);
  if (!email) return null;
  const [local, domain] = splitEmail(email);
  return stripAccents(stripLocalPart(local, true) + "@" + domain);
}

// E.164 allows at most 15 digits; anything shorter than 7 is not a real
// internationally-routable number and would only produce dead hashes.
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15;

/**
 * Normalize a phone number to its international digit form (country code +
 * national number, no "+", no leading trunk zeros). Returns null when no
 * country can be determined or the result is not a plausible number:
 * honest nothing beats a hash that can never match.
 *
 * Resolution order: "+"-prefix in the input wins, then a "00" international
 * prefix, then the caller-supplied default dial code (visitor GeoIP country
 * or webhook country param).
 */
export function normalizePhoneDigits(raw: string, defaultDialCode?: string | null): string | null {
  const input = String(raw).trim();
  if (!input) return null;

  const hasPlus = input.startsWith("+");
  let digits = input.replace(/\D/g, "");
  if (!digits) return null;

  let result: string | null = null;
  if (hasPlus) {
    result = digits;
  } else if (digits.startsWith("00")) {
    result = digits.slice(2);
  } else if (defaultDialCode) {
    /**
     * Is this already international, or is it national?
     *
     * THE OLD TEST WAS A LENGTH, AND IT NEVER FIRED. It asked for more than
     * thirteen digits before believing a number already carried its country
     * code, while real numbers run ten to thirteen. So "31612345678" with a
     * Dutch dial code became "3131612345678", and "16505551212" with a US one
     * became "116505551212". That is the only phone key we send to Google
     * (ph_e), hashed, so it matched nothing and nothing said so. It hit the
     * webhook lane hardest, where "+"-less international numbers are normal.
     *
     * The honest test is the TRUNK PREFIX, not the length. A national number
     * carries one (0 in most of the world); an international one does not. So:
     *
     *   starts with 0                  national, strip the trunk, prepend
     *   starts with the country code   already international, keep as is
     *   anything else                  national without a trunk, prepend
     *
     * The middle case is genuinely ambiguous for a handful of country codes
     * that also open valid national numbers, and no rule fixes that without
     * knowing the country's numbering plan. Reading it as international is the
     * safer half: prepending to a number that already has its code produces a
     * hash that can never match, while the reverse at least keeps a plausible
     * number that sometimes does.
     */
    const startsWithTrunk = digits.startsWith("0");
    const national = digits.replace(/^0+/, "");
    if (!national) return null;

    if (!startsWithTrunk && digits.startsWith(defaultDialCode) && digits.length > defaultDialCode.length) {
      result = digits;
    } else {
      result = defaultDialCode + national;
    }
  } else {
    return null;
  }

  result = result.replace(/^0+/, "");
  if (result.length < PHONE_MIN_DIGITS || result.length > PHONE_MAX_DIGITS) return null;
  return result;
}

/** E.164 variant: "+" + the digit form. */
export function normalizePhoneE164(raw: string, defaultDialCode?: string | null): string | null {
  const digits = normalizePhoneDigits(raw, defaultDialCode);
  if (!digits) return null;
  return "+" + digits;
}

/**
 * Names: trim, lowercase, punctuation removed ("Jan-Willem" -> "janwillem").
 *
 * The accent STAYS. Stripping it was our own invention and it silently cost
 * every match on an accented name, which in these markets is a large share of
 * them. Meta publishes the hash it expects for "Valéry" and it is the hash of
 * "valéry", accent intact; the accent-stripped "valery" hashes to something
 * else entirely and simply never matches. Nothing reports that: the event is
 * accepted, the person is not recognised, and the only symptom is a match
 * quality score that is lower than it should be for reasons nobody can see.
 */
export function normalizeName(raw: string): string | null {
  const name = composeAccents(String(raw).trim().toLowerCase())
    .replace(/[^\p{L}\p{N} ]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!name) return null;
  return name;
}

/**
 * OpenAI name variant (fn_o/ln_o): the base name with ALL whitespace removed,
 * accents kept. OpenAI's published rule is lowercase + strip whitespace and
 * ASCII punctuation, preserve non-ASCII; our base fn/ln keeps internal spaces
 * ("mary jane"), so a multi-word name hashes differently and would silently
 * never match. Only sent when it differs from the base (adapters fall back).
 */
export function normalizeNameOpenAI(raw: string): string | null {
  const base = normalizeName(raw);
  if (base === null) return null;
  const spaceless = base.replace(/ /g, "");
  if (!spaceless) return null;
  return spaceless;
}

/** City: lowercase, spaces and punctuation removed ("Den Haag" -> "denhaag").
 *  Accents kept, for the same reason as names. */
export function normalizeCity(raw: string): string | null {
  const city = composeAccents(String(raw).trim().toLowerCase()).replace(/[^\p{L}\p{N}]/gu, "");
  if (!city) return null;
  return city;
}

/** State/region: lowercase, spaces removed. */
export function normalizeState(raw: string): string | null {
  const state = stripAccents(String(raw).trim().toLowerCase()).replace(/[^\p{L}\p{N}]/gu, "");
  if (!state) return null;
  return state;
}

/** Zip/postal code: lowercase, spaces and dashes removed ("1234 AB" -> "1234ab"). */
export function normalizeZip(raw: string): string | null {
  const zip = String(raw).trim().toLowerCase().replace(/[\s-]/g, "");
  if (!zip) return null;
  return zip;
}

/** Country: ISO 3166-1 alpha-2, lowercase. Returns null for non-2-letter input. */
export function normalizeCountry(raw: string): string | null {
  const country = String(raw).trim().toLowerCase();
  if (!/^[a-z]{2}$/.test(country)) return null;
  return country;
}

/** Gender to the platform enum: "f", "m" or "n" (non-binary/other). */
export function normalizeGender(raw: string): string | null {
  const gender = String(raw).trim().toLowerCase();
  if (gender.startsWith("f") || gender.startsWith("v" /* NL: vrouw */)) return "f";
  if (gender.startsWith("m")) return "m";
  if (gender.startsWith("n") || gender.startsWith("o" /* other */) || gender.startsWith("x")) return "n";
  return null;
}

/** Birthday to YYYYMMDD digits; accepts YYYY-MM-DD, DD-MM-YYYY and YYYYMMDD. */
export function normalizeBirthday(raw: string): string | null {
  const input = String(raw).trim();
  const digits = input.replace(/\D/g, "");
  if (digits.length !== 8) return null;
  // DD-MM-YYYY / DD/MM/YYYY: year at the end
  if (/^\d{1,2}[-/.]\d{1,2}[-/.]\d{4}$/.test(input)) {
    const parts = input.split(/[-/.]/);
    return parts[2] + parts[1].padStart(2, "0") + parts[0].padStart(2, "0");
  }
  // YYYY-MM-DD or YYYYMMDD: year first
  if (!digits.startsWith("19") && !digits.startsWith("20")) return null;
  return digits;
}

/** A 64-char lowercase hex string, i.e. an already-computed SHA-256 hash. */
export function isSha256Hex(value: unknown): value is string {
  return typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
}
